/**
 * utils/tiroirsConfProcessor.js - Processeur spécialisé configuration tiroirs
 * Validation et transformation des données de configuration tiroirs
 */

import { extractFileInfo, readJsonFile } from "./jsonHandler.js";

import logger from "../utils/logger.js";

/**
 * Valider la structure complète du JSON tiroirs
 * @param {Object} data - Données JSON à valider
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
function _validateTiroirsStructure(data) {
  const errors = [];

  if (!data || typeof data !== "object") {
    errors.push("Le fichier doit contenir un objet JSON valide");
    return { isValid: false, errors };
  }

  // Valider chaque section
  const metadataValidation = _validateMetadata(data.metadata);
  const summaryValidation = _validateSummary(data.summary);
  const dataValidation = _validateDataArray(data.data);

  errors.push(...metadataValidation.errors);
  errors.push(...summaryValidation.errors);
  errors.push(...dataValidation.errors);

  return {
    isValid: errors.length === 0,
    errors: errors,
  };
}

/**
 * Valider la section metadata
 * @param {Object} metadata - Section metadata à valider
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
function _validateMetadata(metadata) {
  const errors = [];

  if (!metadata) {
    errors.push("Section 'metadata' manquante");
    return { isValid: false, errors };
  }

  const requiredFields = {
    exportDate: "string",
    version: "string",
    totalFibers: "number",
    application: "string",
  };

  Object.entries(requiredFields).forEach(([field, expectedType]) => {
    if (metadata[field] === undefined || metadata[field] === null) {
      errors.push(`metadata.${field} manquant`);
    } else if (typeof metadata[field] !== expectedType) {
      errors.push(`metadata.${field} doit être de type ${expectedType}`);
    }
  });

  if (typeof metadata.totalFibers === "number" && metadata.totalFibers < 0) {
    errors.push("metadata.totalFibers doit être un nombre positif");
  }

  return {
    isValid: errors.length === 0,
    errors: errors,
  };
}

/**
 * Valider la section summary
 * @param {Object} summary - Section summary à valider
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
function _validateSummary(summary) {
  const errors = [];

  if (!summary) {
    errors.push("Section 'summary' manquante");
    return { isValid: false, errors };
  }

  const requiredNumericFields = ["configured", "libre", "occupee", "na"];

  requiredNumericFields.forEach((field) => {
    if (summary[field] === undefined || summary[field] === null) {
      errors.push(`summary.${field} manquant`);
    } else if (typeof summary[field] !== "number") {
      errors.push(`summary.${field} doit être un nombre`);
    } else if (summary[field] < 0) {
      errors.push(`summary.${field} doit être un nombre positif ou zéro`);
    } else if (!Number.isInteger(summary[field])) {
      errors.push(`summary.${field} doit être un nombre entier`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors: errors,
  };
}

/**
 * Valider le tableau de données principal
 * @param {Array} dataArray - Tableau des données de configuration
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
function _validateDataArray(dataArray) {
  const errors = [];

  if (!Array.isArray(dataArray)) {
    errors.push("Section 'data' doit être un tableau");
    return { isValid: false, errors };
  }

  if (dataArray.length === 0) {
    errors.push("Le tableau 'data' ne peut pas être vide");
    return { isValid: false, errors };
  }

  // Valider un échantillon d'éléments (les 5 premiers pour optimiser)
  const sampleSize = Math.min(5, dataArray.length);
  const sampleItems = dataArray.slice(0, sampleSize);

  sampleItems.forEach((item, index) => {
    const itemErrors = _validateDataItem(item, index);
    errors.push(...itemErrors);
  });

  return {
    isValid: errors.length === 0,
    errors: errors,
  };
}

/**
 * Valider un élément individuel du tableau data
 * @param {Object} item - Élément à valider
 * @param {number} index - Index de l'élément dans le tableau
 * @returns {string[]} Tableau des erreurs
 */
function _validateDataItem(item, index) {
  const errors = [];

  if (!item || typeof item !== "object") {
    errors.push(`data[${index}] doit être un objet`);
    return errors;
  }

  const fieldValidations = {
    tiroir: {
      type: "string",
      required: true,
      validate: (value) => (value.trim().length > 0 ? null : "ne peut pas être vide"),
    },
    module: {
      type: "string",
      required: true,
      validate: (value) => (value.trim().length > 0 ? null : "ne peut pas être vide"),
    },
    port: {
      type: "number",
      required: true,
      validate: (value) => {
        if (!Number.isInteger(value)) return "doit être un nombre entier";
        if (value < 1) return "doit être supérieur à 0";
        return null;
      },
    },
    statut: {
      type: "string",
      required: true,
      validate: (value) => {
        const validStatuses = ["LIBRE", "OCCUPÉE", "N/A"];
        if (!validStatuses.includes(value.trim())) {
          return `doit être un des statuts valides: ${validStatuses.join(", ")} (reçu: "${value}")`;
        }
        return null;
      },
    },
  };

  Object.entries(fieldValidations).forEach(([fieldName, validation]) => {
    const value = item[fieldName];

    if (validation.required && (value === undefined || value === null)) {
      errors.push(`data[${index}].${fieldName} manquant`);
      return;
    }

    if (value !== undefined && typeof value !== validation.type) {
      errors.push(`data[${index}].${fieldName} doit être de type ${validation.type}`);
      return;
    }

    if (value !== undefined && validation.validate) {
      const customError = validation.validate(value);
      if (customError) {
        errors.push(`data[${index}].${fieldName} ${customError}`);
      }
    }
  });

  return errors;
}

/**
 * Normaliser le statut selon le mapping standard
 * @param {string} status - Statut brut
 * @returns {string} Statut normalisé
 */
function _normalizeStatus(status) {
  const statusMapping = {
    LIBRE: "libre",
    OCCUPÉE: "occupee",
    "N/A": "na",
  };

  return statusMapping[status?.trim()] || status;
}

/**
 * Construire la liste de références à partir des données JSON
 * @param {Object} jsonData - Données JSON complètes
 * @returns {Array} Liste des références transformées
 */
function _buildReferenceList(jsonData) {
  if (!jsonData || !jsonData.data || !Array.isArray(jsonData.data)) {
    logger.warn(
      {
        context: "tiroirs-reference-building",
        issue: "invalid-json-data",
        hasJsonData: !!jsonData,
        hasDataProperty: !!jsonData?.data,
        isDataArray: Array.isArray(jsonData?.data),
      },
      "⚠️ Données JSON invalides pour construire les références"
    );
    return [];
  }

  return jsonData.data
    .map((item, index) => {
      if (!item.tiroir || !item.module || typeof item.port !== "number" || !item.statut) {
        logger.warn(
          {
            context: "tiroirs-reference-building",
            issue: "invalid-item",
            itemIndex: index,
            itemData: {
              hasTiroir: !!item?.tiroir,
              hasModule: !!item?.module,
              hasPort: typeof item?.port === "number",
              hasStatut: !!item?.statut,
              tiroir: item?.tiroir,
              module: item?.module,
              port: item?.port,
              statut: item?.statut,
            },
          },
          `⚠️ Élément invalide à l'index ${index}`
        );
        return null;
      }

      return {
        REFERENCE_SHORT: `B2-${item.tiroir}-M${item.module}`,
        REFERENCE_LONG: `B2-${item.tiroir}-MODULE-${item.module}`,
        PORT: item.port,
        ETAT: _normalizeStatus(item.statut),
        // Champs additionnels pour le traçage
        TIROIR: item.tiroir,
        MODULE: item.module,
        INDEX_ORIGINAL: index,
        STATUT_ORIGINAL: item.statut,
      };
    })
    .filter((item) => item !== null);
}

/**
 * Transformer les données de configuration complètes
 * @param {Object} rawData - Données brutes du fichier
 * @param {File} file - Objet File original
 * @returns {Object} Données transformées et enrichies
 */
function _transformTiroirsData(rawData, file) {
  if (!rawData || !file) {
    throw new Error("transformTiroirsData: rawData et file sont requis");
  }

  // Construire la liste de références
  const referenceList = _buildReferenceList(rawData);

  // Informations sur le fichier
  const fileInfo = extractFileInfo(file);

  // Créer l'objet de données transformées
  return {
    // Informations du fichier
    fileName: file.name,
    fileSize: file.size,
    fileSizeFormatted: fileInfo.sizeFormatted,
    fileLastModified: file.lastModified,
    fileCreationDate: fileInfo.lastModifiedFormatted,

    // Métadonnées
    metadata: {
      ...rawData.metadata,
      validatedAt: new Date().toISOString(),
      processingTimestamp: Date.now(),
    },

    // Résumé
    summary: calculateSummaryFromReferences(referenceList),

    // Données transformées
    references: referenceList,
    referenceCount: referenceList.length,

    // Données brutes (backup)
    rawData: rawData,
  };
}

/**
 * Valider et traiter un fichier de configuration tiroirs complet
 * @param {File} file - Fichier à traiter
 * @returns {Promise<Object>} Résultat de validation et traitement
 */
export async function processTiroirsFile(file) {
  try {
    // Lire le fichier JSON
    const jsonResult = await readJsonFile(file, {
      maxSize: 10 * 1024 * 1024,
      allowedExtensions: [".json"],
    });

    if (!jsonResult.isValid) {
      return {
        isValid: false,
        errors: jsonResult.errors,
        data: null,
      };
    }

    // Valider la structure spécifique aux tiroirs
    const structureValidation = _validateTiroirsStructure(jsonResult.data);
    if (!structureValidation.isValid) {
      return {
        isValid: false,
        errors: ["Structure JSON invalide pour les tiroirs:", ...structureValidation.errors],
        data: null,
      };
    }

    // Transformer les données
    const transformedData = _transformTiroirsData(jsonResult.data, file);

    return {
      isValid: true,
      errors: [],
      data: transformedData,
    };
  } catch (error) {
    return {
      isValid: false,
      errors: [`Erreur de traitement: ${error.message}`],
      data: null,
    };
  }
}

function calculateSummaryFromReferences(references) {
  const summary = { configured: 0, libre: 0, occupee: 0, na: 0 };

  references.forEach((ref) => {
    summary.configured++;
    switch (ref.ETAT) {
      case "libre":
        summary.libre++;
        break;
      case "occupee":
        summary.occupee++;
        break;
      case "na":
        summary.na++;
        break;
    }
  });

  return summary;
}
